<template>
  <header class="header" :class="{ 'minimal-header': isMinimal }">
    <!-- 顶部导航菜单 -->
    <nav class="top-nav">
      <div class="nav-menu">
        <button class="nav-link" @click.stop="goToHome" type="button">
          首页
        </button>
        
        <!-- 频道下拉菜单 -->
        <div class="channel-dropdown">
          <button 
            ref="channelDropdownButton"
            class="nav-link dropdown-toggle" 
            type="button" 
            @click="toggleChannelDropdown"
          >
            频道
            <i class="fas fa-chevron-down dropdown-arrow" :class="{ 'rotated': showChannelDropdown }"></i>
          </button>
        </div>
        
        <button 
          v-if="!isVerified"
          class="nav-link"
          @click.stop="showEmailVerification"
          type="button"
        >
          邮箱验证
        </button>
        
        <button class="nav-link" @click.stop="openSubmission" type="button">
          纪录片收录
        </button>
        
        <button class="nav-link" @click.stop="goToAbout" type="button">
          关于网站
        </button>
        


        <template v-if="isVerified">
          <div class="nav-divider">|</div>
          <button class="nav-link" @click.stop="goToUserCenter" type="button">
            <i class="fas fa-user-circle"></i>
            个人中心
          </button>
          <div class="user-info-inline">
            <span class="user-email">{{ userEmail }}</span>
            <button class="nav-link logout-link" @click.stop="logout" type="button">
              退出
            </button>
          </div>
        </template>
      </div>
    </nav>

    <div v-if="!isMinimal" class="header-content">
      <div class="logo">
        <i class="fas fa-heart"></i>
        我爱纪录片
      </div>
      <p class="tagline">探索世界精彩纪录片，发现知识无限魅力</p>
      <div class="header-actions">
        <div class="search-section">
          <input
            type="text"
            class="search-box"
            v-model="searchQuery"
            @keyup.enter="performSearch"
            placeholder="搜索纪录片名称、导演或关键词..."
            autocomplete="off"
            spellcheck="false"
          />
          <button class="search-btn" @click="performSearch">
            <i class="fas fa-search"></i>
          </button>
        </div>
       
      </div>
    </div>
  </header>

  <!-- 频道下拉菜单 -->
  <Teleport to="body">
    <div 
      class="dropdown-menu-teleport" 
      v-if="showChannelDropdown"
      :style="dropdownPosition"
    >
      <button 
        v-for="channel in getChannelList()" 
        :key="getChannelKey(channel)"
        class="dropdown-item"
        @click.stop="goToChannel(getChannelCode(channel))"
      >
        <i :class="channelStore.getChannelIcon(getChannelCode(channel))"></i>
        {{ getChannelName(channel) }}
      </button>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import { useDocumentaryStore } from '@/stores/documentary'
import { useUserStore } from '@/stores/user'
import { useChannelStore } from '@/stores/channel'
import { storeToRefs } from 'pinia'
import { debugError } from '@/utils/env'
// 定义 props
interface Props {
  isMinimal?: boolean
}
withDefaults(defineProps<Props>(), {
  isMinimal: false
})

const router = useRouter()
const documentaryStore = useDocumentaryStore()
const userStore = useUserStore()
const channelStore = useChannelStore()

// 使用 storeToRefs 获取响应式属性
const { isVerified, userEmail } = storeToRefs(userStore)

const searchQuery = ref('')
const showChannelDropdown = ref(false)
const channelDropdownButton = ref<HTMLElement | null>(null)

const performSearch = useDebounceFn(() => {
  if (searchQuery.value.trim()) {
    documentaryStore.setSearchQuery(searchQuery.value)
    router.push('/search')
  }
}, 300)

const openSubmission = () => {
  if ((window as any).globalModal?.openSubmission) {
    (window as any).globalModal.openSubmission()
  }
}

const showEmailVerification = () => {
  if ((window as any).globalModal?.openEmailVerification) {
    (window as any).globalModal.openEmailVerification().then(() => {
      // 验证成功后刷新用户状态
      userStore.initUser()
    })
  }
}

const goToHome = () => {
  router.push('/')
}

const goToAbout = () => {
  router.push('/about')
}

const goToUserCenter = () => {
  router.push('/user')
}

const logout = () => {
  userStore.logout()
  router.push('/')
}

const toggleChannelDropdown = () => {
  showChannelDropdown.value = !showChannelDropdown.value
}

const goToChannel = (channelCode: string) => {
  showChannelDropdown.value = false
  router.push(`/channel/${channelCode}`)
}

// 获取频道列表
const getChannelList = () => {
  if (channelStore.channels.length > 0) {
    return channelStore.channels
  } else {
    // 返回默认频道列表
    return [
      { code: 'bbc', name: 'BBC' },
      { code: 'nhk', name: 'NHK' },
      { code: 'cctv', name: 'CCTV' },
      { code: 'discovery', name: 'Discovery' },
      { code: 'NG', name: 'National Geographic' },
      { code: 'hbo', name: 'HBO' },
      { code: 'netflix', name: 'Netflix' },
      { code: 'amazon', name: 'Amazon Prime' },
      { code: 'apple', name: 'Apple TV+' },
      { code: 'youtube', name: 'YouTube' }
    ]
  }
}

// 获取频道key
const getChannelKey = (channel: any) => {
  return channel.code || channel.value || channel.id
}

// 获取频道code
const getChannelCode = (channel: any) => {
  return channel.code || channel.value || 'all'
}

// 获取频道名称
const getChannelName = (channel: any) => {
  return channel.name || channel.label || '未知频道'
}

// 计算下拉菜单位置
const dropdownPosition = computed(() => {
  if (!channelDropdownButton.value) return {}
  
  const rect = channelDropdownButton.value.getBoundingClientRect()
  const dropdownWidth = 180 // 下拉菜单的宽度
  
  return {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left + rect.width / 2 - dropdownWidth / 2}px`
  }
})

// 处理点击外部关闭下拉菜单
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element
  const dropdownButton = channelDropdownButton.value
  
  // 如果点击的不是频道按钮或下拉菜单内容，则关闭菜单
  if (dropdownButton && !dropdownButton.contains(target)) {
    const dropdownMenu = document.querySelector('.dropdown-menu-teleport')
    if (dropdownMenu && !dropdownMenu.contains(target)) {
      showChannelDropdown.value = false
    }
  }
}

// 初始化频道数据
onMounted(async () => {
  try {
    await channelStore.fetchChannels()
  } catch (error) {
    debugError('初始化频道数据失败:', error)
  }
  // 添加全局点击事件监听器
  document.addEventListener('click', handleClickOutside)
})

// 清理事件监听器
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.header {
  background: var(--gradient-dark);
  color: var(--white);
  padding: 20px 0 30px 0;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.minimal-header {
  padding: 0;
  min-height: 50px;
}

.minimal-header .top-nav {
  position: relative;
  background: rgba(44, 62, 80, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

/* 顶部导航菜单 */
.top-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-fixed);
  background: rgba(44, 62, 80, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  pointer-events: auto;
}

.nav-menu {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
  width: 100%;
}

.nav-link {
  background: none;
  border: none;
  color: var(--white);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 20px;
  transition: background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  text-decoration: none;
  display: inline-block;
  pointer-events: auto;
  z-index: var(--z-dropdown);
  user-select: none;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.nav-link:hover::after {
  width: 80%;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background: var(--secondary-color);
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.nav-divider {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  margin: 0 5px;
}

.user-info-inline {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-email {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-link {
  padding: 6px 12px;
  background: rgba(220, 53, 69, 0.8);
  border-radius: 15px;
  font-size: 12px;
}

.logout-link:hover {
  background: rgba(220, 53, 69, 1);
}

/* 频道下拉菜单 */
.channel-dropdown {
  position: relative;
  display: inline-block;
  pointer-events: auto;
}

.dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dropdown-arrow {
  font-size: 10px;
  transition: transform 0.3s ease;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background: rgba(44, 62, 80, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  min-width: 180px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: var(--z-dropdown);
  overflow: hidden;
  margin-top: 8px;
  animation: dropdownSlideIn 0.3s ease-out;
  pointer-events: auto;
}

@keyframes dropdownSlideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  width: 100%;
  background: none;
  border: none;
  color: var(--white);
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: rgba(255, 255, 255, 0.1);
  padding-left: 20px;
}

.dropdown-item i {
  width: 16px;
  text-align: center;
  color: var(--secondary-color);
}

.dropdown-menu-teleport {
  position: fixed;
  background: rgba(44, 62, 80, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  min-width: 180px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: var(--z-dropdown);
  overflow: hidden;
  animation: dropdownSlideIn 0.3s ease-out;
  pointer-events: auto;
}

.header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: 
    radial-gradient(circle, rgba(139, 115, 85, 0.15) 0%, transparent 50%),
    radial-gradient(circle, rgba(160, 130, 109, 0.1) 30%, transparent 60%);
}

.header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(139, 115, 85, 0.8), 
    rgba(160, 130, 109, 0.8), 
    transparent);
}

.header-content {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 30px 20px 0 20px;
}

.logo {
  font-size: 2.5em;
  font-weight: 800;
  margin-bottom: 10px;
  color: var(--white);
  letter-spacing: -0.02em;
  font-family: 'Georgia', 'Times New Roman', serif;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.tagline {
  font-size: 1.1em;
  opacity: 1;
  margin-bottom: 15px;
  color: var(--white);
  font-weight: 500;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
}

.search-section {
  flex: 1;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
}

.search-box {
  width: 100%;
  padding: 18px 55px 18px 25px;
  border: 2px solid transparent;
  border-radius: 50px;
  font-size: 1.05em;
  background: var(--glass-bg);
  box-shadow: var(--shadow);
  transition: all 0.3s ease;
  color: var(--dark-text);
}

.search-box:focus {
  outline: none;
  border-color: var(--secondary-color);
  box-shadow: 0 0 0 4px rgba(139, 115, 85, 0.1);
}

.search-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--gradient-primary);
  border: none;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  color: var(--white);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-btn:hover {
  background: var(--gradient-secondary);
  transform: translateY(-50%) scale(1.05);
}

.search-btn:active {
  transform: translateY(-50%) scale(0.95);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .top-nav {
    padding: 8px 15px;
  }
  
  .nav-menu {
    gap: 10px;
  }
  
  .nav-link {
    font-size: 12px;
    padding: 6px 12px;
  }
  
  .dropdown-menu {
    min-width: 150px;
  }
  
  .dropdown-item {
    padding: 10px 12px;
    font-size: 12px;
  }
  
  .user-email {
    max-width: 100px;
    font-size: 11px;
  }
  
  .header {
    padding: 30px 0;
  }

  .logo {
    font-size: 2.2em;
  }

  .header-actions {
    flex-direction: column;
    gap: 15px;
  }

  .search-section {
    max-width: 100%;
  }

  .search-box {
    padding: 15px 50px 15px 20px;
  }
}

@media (max-width: 480px) {
  .top-nav {
    padding: 6px 10px;
  }
  
  .nav-menu {
    gap: 6px;
    flex-wrap: wrap;
  }
  
  .nav-link {
    font-size: 10px;
    padding: 4px 8px;
  }
  
  .dropdown-menu {
    min-width: 120px;
  }
  
  .dropdown-item {
    padding: 8px 10px;
    font-size: 10px;
    gap: 8px;
  }
  
  .user-info-inline {
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
  }
  
  .user-email {
    max-width: 80px;
    font-size: 9px;
    text-align: right;
  }
  
  .logout-link {
    font-size: 10px;
    padding: 4px 8px;
  }
  
  .logo {
    font-size: 1.8em;
  }

  .tagline {
    font-size: 1em;
  }

  .header-actions {
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .header {
    padding: 30px 0;
  }

  .logo {
    font-size: 2.2em;
  }

  .header-actions {
    flex-direction: column;
    gap: 15px;
  }

  .search-section {
    max-width: 100%;
  }

  .search-box {
    padding: 15px 50px 15px 20px;
  }
}

@media (max-width: 480px) {
  .logo {
    font-size: 1.8em;
  }

  .tagline {
    font-size: 1em;
  }

  .header-actions {
    gap: 12px;
  }
  

}
</style>